// Shared input sanitizers for phone and Swish number fields.
// Keep these the single source of truth — do not duplicate the rules inline.

// Phone: only digits and a single leading '+'. Max 16 chars (E.164 is +15 digits).
export function sanitizePhone(value) {
  if (value == null) return '';
  let v = String(value).replace(/[^\d+]/g, '');      // drop everything except digits and '+'
  const leadingPlus = v.startsWith('+');
  v = (leadingPlus ? '+' : '') + v.replace(/\+/g, ''); // '+' allowed only as first char
  return v.slice(0, 16);
}

// Swish alias: only digits and a single leading '+'. NO forced prefix — valid aliases
// take multiple shapes (personal '+46...', Swish for Business '123...', corporate '8...'),
// so the exact prefix is validated server-side, not coerced in the UI.
export function sanitizeSwish(value) {
  if (value == null) return '';
  let v = String(value).replace(/[^\d+]/g, '');
  const leadingPlus = v.startsWith('+');
  v = (leadingPlus ? '+' : '') + v.replace(/\+/g, '');
  return v.slice(0, 16);
}

// Swedish phone -> canonical E.164 '+46...'. Mirrors backend core/utils.normalize_se_phone:
// strip spaces/dashes/parens; '00' -> '+'; national leading '0' -> '+46'; bare '46' -> '+46'.
// Apply on blur/submit (not per keystroke, or typing '00' would be mangled).
export function normalizeSwedishPhone(value) {
  if (value == null) return value;
  const v = String(value).replace(/[\s\-()]/g, '');
  if (v.startsWith('00')) return '+' + v.slice(2);
  if (v.startsWith('0')) return '+46' + v.slice(1);
  if (v.startsWith('46')) return '+' + v;
  return v;
}

// Swish alias: personal mobiles -> '+46...'; corporate '123...' numbers keep the existing
// rule (trim only). Mirrors backend core/utils.normalize_swish.
export function normalizeSwish(value) {
  if (value == null) return value;
  if (String(value).replace(/[\s\-()]/g, '').startsWith('123')) return String(value).trim();
  return normalizeSwedishPhone(value);
}

// Strict Swedish mobile rule (mirrors backend core/utils.is_valid_se_mobile):
// 07X + 7 digits nationally == canonical '+467' + 8 digits.
export const PHONE_INVALID_MSG = 'Invalid phone number. Enter a Swedish mobile number, e.g. 070-123 45 67';
export const PHONE_REQUIRED_MSG = 'Phone number is required';

export function isValidSwedishMobile(value) {
  if (!value || !String(value).trim()) return false;
  return /^\+467\d{8}$/.test(normalizeSwedishPhone(value));
}

// Returns '' when valid, otherwise the exact backend error message.
export function swedishMobileError(value) {
  if (!value || !String(value).trim()) return PHONE_REQUIRED_MSG;
  return isValidSwedishMobile(value) ? '' : PHONE_INVALID_MSG;
}

// Organisation number: digits only (Swedish organisationsnummer = 10 digits, stored
// without the XXXXXX-XXXX separator). Strip everything that is not 0-9.
export function sanitizeOrgNumber(value) {
  if (value == null) return '';
  return String(value).replace(/\D/g, '').slice(0, 10);
}
